import { XAxis, YAxis, Line, LineChart as RechartsLine, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import type { ILineChart } from './types';
import ChartXTick from '@klv/atoms/chart-x-tick';
import ChartYTick from '@klv/atoms/chart-y-tick';
import { expandColors, formatValue, lineChartColors } from '@klv/shared/utils/charts';
import { clampSize } from '@klv/shared/utils/functions';
import { designSystemColors } from '@klv/shared/styles/colors';
import { useWindowDimensions } from '@klv/hooks/useWindowDimensions';

import TotalValues from './totalValues';
import * as s from './styles';

const TopLineLabel = (props: any) => {
  const { i18n } = useTranslation();
  const { x, y, value, data, backgroundColor } = props;
  const fillColor =
    backgroundColor === 'charcoal'
      ? designSystemColors.poppy
      : designSystemColors.poppyShade;

  return (
    <text
      fontFamily='Sohne'
      x={x}
      y={y}
      dy={-14}
      fill={fillColor}
      fontSize={clampSize(1.3, 2)}
      fontWeight={500}
      textAnchor="middle"
    >
      {formatValue(props.numberFormat, value, i18n.language)}
    </text>
  );
};
const LineChart = ({
  data,
  headers,
  backgroundColor,
  totalValues,
  yAxisHeadline,
  numberFormat,
}: ILineChart) => {
  const { width } = useWindowDimensions();
  const selectedColor =
    backgroundColor === 'charcoal'
      ? lineChartColors.charcoal
      : lineChartColors.lightSand;
  const colorMap = expandColors(headers, selectedColor);
  const strokeColor =
    backgroundColor === 'charcoal'
      ? designSystemColors.white
      : designSystemColors.charcoal;
  const isMobile = width <= 576;
  const isTablet = width <= 1024;
  const isDesktop = width >= 1366;
  const fontSizeClamp = clampSize(1.6, 2);

  return (
    <div>
      <div
        style={{
          display: isDesktop ? 'flex' : 'block',
          justifyContent: 'center',
          overflowX: isMobile ? 'auto' : 'hidden',
          ...(isMobile && {
            scrollBehavior: 'smooth',
            paddingBottom: '1rem',
            maxWidth: '100%',
          }),
        }}
      >
        {yAxisHeadline && <span style={{ color: strokeColor }}><s.YAxisHeadline variant="h6Semibold">{yAxisHeadline}</s.YAxisHeadline></span>}
        <ResponsiveContainer
          width={isTablet ? '100%' : '85%'}
          aspect={2}
          minWidth={isMobile ? 492 : 475}
          >
          <RechartsLine data={data}>
            <XAxis
              padding={{ left: 36, right: 56 }}
              dataKey={headers[0]}
              tickLine={false}
              axisLine={{ stroke: strokeColor, strokeWidth: 0.5 }}
              tick={(props) => (
                <ChartXTick
                  {...props}
                  backgroundColor={backgroundColor}
                  fontSizeClamped={fontSizeClamp}
                  fontWeight={500}
                />
              )}
            />
            <YAxis
              padding={{ top: 36, bottom: 36 }}
              axisLine={{ stroke: strokeColor, strokeWidth: 0.5 }}
              tickLine={false}
              tick={(props) => (
                <ChartYTick
                  {...props}
                  backgroundColor={backgroundColor}
                  fontSizeClamped={fontSizeClamp}
                />
              )}
            />
            {headers.slice(1).map((key: string, i: number) => {
              return (
                <Line
                  dot={true}
                  type="monotone"
                  dataKey={key}
                  stroke={colorMap[i].rgb}
                  fill={colorMap[i].rgb}
                  strokeWidth={3}
                  activeDot={{ stroke: colorMap[i].rgb, r: 5 }}
                  label={
                    i === 0 ? (
                      <TopLineLabel
                        backgroundColor={backgroundColor}
                        data={data}
                        dataKey={key}
                        numberFormat={numberFormat}
                      />
                    ) : (
                      <></>
                    )
                  }
                />
              );
            })}
          </RechartsLine>
        </ResponsiveContainer>
        <TotalValues totalValues={totalValues} colorMap={colorMap} isDesktop={isDesktop} isTablet={isTablet} strokeColor={strokeColor}/>
      </div>
    </div>
  );
};

export default LineChart;
